import { Link } from "react-router-dom";
import { Phone, Mail, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/services";
import { useSite } from "@/hooks/content";
import SocialLinks from "@/components/SocialLinks";

const nav = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Testimonials", path: "/testimonials" },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

export default function Footer() {
  // Contact details and social links are editable in the admin panel; the
  // values in src/data/site.js are the fallback while they load.
  const { site, socials, waLink, telLink } = useSite();

  return (
    <footer className="relative overflow-hidden bg-navy-deep text-white" data-testid="site-footer">
      <div className="pointer-events-none absolute inset-0 bg-blueprint-dark opacity-40" />
      <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-brand/20 blur-[120px]" />

      <div className="container-sky relative py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="inline-flex rounded-2xl bg-white p-4">
              <img src={site.logo} alt={`${site.name} logo`} className="h-16 w-auto" />
            </div>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-300">
              Professional Workplace Safety &amp; Health and Fire Safety consultancy, training and
              emergency preparedness solutions for organisations in Singapore.
            </p>
            <p className="mt-5 font-mono-tech text-xs uppercase tracking-widest text-slate-400">
              UEN: {site.uen}
            </p>
          </div>

          {/* Navigate */}
          <div className="lg:col-span-2">
            <h2 className="tech-tag text-brand-light">Navigate</h2>
            <ul className="mt-5 space-y-3">
              {nav.map((n) => (
                <li key={n.name}>
                  <Link to={n.path} className="text-sm text-slate-300 transition-colors hover:text-white">
                    {n.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h2 className="tech-tag text-brand-light">Services</h2>
            <ul className="mt-5 space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-sm text-slate-300 transition-colors hover:text-white">
                    {s.shortTitle}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h2 className="tech-tag text-brand-light">Get in Touch</h2>
            <ul className="mt-5 space-y-4">
              <li>
                <a href={telLink} className="group flex items-center gap-3 text-sm text-slate-300 hover:text-white">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 group-hover:bg-brand"><Phone className="h-4 w-4" /></span>
                  {site.mobile}
                </a>
              </li>
              <li>
                <a href={`mailto:${site.email}`} className="group flex items-center gap-3 text-sm text-slate-300 hover:text-white">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 group-hover:bg-brand"><Mail className="h-4 w-4" /></span>
                  {site.email}
                </a>
              </li>
            </ul>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-whatsapp mt-6 w-full">
              WhatsApp Us <ArrowUpRight className="h-4 w-4" />
            </a>
            {/* Renders nothing until at least one social link is saved. */}
            <SocialLinks socials={socials} tone="dark" className="mt-6" />
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} {site.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-xs text-slate-400 transition-colors hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-xs text-slate-400 transition-colors hover:text-white">Terms of Use</Link>
            <Link to="/sitemap" className="text-xs text-slate-400 transition-colors hover:text-white">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
